import { ANALYSIS_PRODUCTION_EXPECTED } from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputStepType } from 'interfaces/IInputStep';
import type { InputTaskCategory } from 'interfaces/IInputTask';

import {
  emptyTally,
  NUTRIENT_DISPLAY_ORDER,
  resolvePaddockId,
  resolvePlanAreaHa,
  type NutritionTally,
} from './computeLiveTotals';
import { resolveAreaHa } from './insightUtils';
import { computeStepRemovalKg } from './nutrition/computeNutritionAudit';

export interface CropRemovalPerHa {
  /** kg/ha removed, on the Nutrition tally's basis (see computeCropRemovalPerHa). */
  removed: NutritionTally;
  /** Plans in scope whose removal can be worked out. */
  planCount: number;
  /** Plans in scope with no production yield or no crop removal values. */
  missingPlanCount: number;
}

/**
 * Crop removal (kg/ha) for Live totals › Nutrient balance (OP-2563), from
 * each plan's production at the Expected yield — the same per-step figure
 * as the Insights Nutrition lens (computeStepRemovalKg), harvested product
 * only.
 *
 * Divided the way computeNutritionPerHa divides nutrients applied, so the
 * two can be subtracted: each plan's kg ÷ its own paddock area (falling back
 * to its largest production area), plans on the same paddock adding, and
 * paddocks area-weighted.
 *
 * Pure function — no side effects, no hooks, no Redux.
 */
export const computeCropRemovalPerHa = (
  inputs: IInput[],
  gUnits: GUnit[],
  categories: Set<InputTaskCategory>
): CropRemovalPerHa => {
  const paddocks = new Map<string, { areaHa: number; perHa: NutritionTally }>();
  let planCount = 0;
  let missingPlanCount = 0;

  for (const input of inputs) {
    const kg = emptyTally();
    let hasRemoval = false;
    let largestProductionAreaHa = 0;

    for (const task of input.tasks) {
      if (!categories.has(task.category)) continue;
      for (const step of task.steps) {
        if (step.stepType !== InputStepType.Production) continue;
        const removed = computeStepRemovalKg(
          step,
          task,
          input,
          gUnits,
          ANALYSIS_PRODUCTION_EXPECTED
        );
        if (!removed) continue;
        hasRemoval = true;
        largestProductionAreaHa = Math.max(
          largestProductionAreaHa,
          resolveAreaHa(step, task, input, gUnits)
        );
        for (const key of NUTRIENT_DISPLAY_ORDER) kg[key] += removed[key] ?? 0;
      }
    }

    if (!hasRemoval) {
      missingPlanCount += 1;
      continue;
    }
    planCount += 1;

    const zoneAreaHa = resolvePlanAreaHa(input, gUnits);
    const areaHa = zoneAreaHa > 0 ? zoneAreaHa : largestProductionAreaHa;
    const groupKey = resolvePaddockId(input) ?? `input:${input.id}`;
    const group = paddocks.get(groupKey) ?? { areaHa: 0, perHa: emptyTally() };
    group.areaHa = Math.max(group.areaHa, areaHa);
    for (const key of NUTRIENT_DISPLAY_ORDER) group.perHa[key] += kg[key] / areaHa;
    paddocks.set(groupKey, group);
  }

  const removed = emptyTally();
  let totalAreaHa = 0;
  paddocks.forEach((group) => {
    totalAreaHa += group.areaHa;
    for (const key of NUTRIENT_DISPLAY_ORDER) {
      removed[key] += group.perHa[key] * group.areaHa;
    }
  });
  if (totalAreaHa > 0) {
    for (const key of NUTRIENT_DISPLAY_ORDER) removed[key] /= totalAreaHa;
  }

  return { removed, planCount, missingPlanCount };
};
